import { useSelector } from "react-redux";

import { useIntl } from "react-intl-compat";

import { selectAuthAccessToken } from "../../../../components/src/app-main/selectors/auth.selectors";
import { selectProfileSSOid } from "../../../../components/src/app-main/selectors/profile.selectors";
import {
  ListingEditorLayout,
  ListingEditorLayoutSkeleton,
} from "../components/ListingEditorLayout";

import { ListingDetailsForm } from "./ListingDetailsForm";
import { useListingDetailsData } from "./useListingDetailsData";

type ListingDetailsPageProps = {
  params: { listingId: string };
};

const ListingDetailsPage = ({
  params: { listingId },
}: ListingDetailsPageProps) => {
  const accessToken = useSelector(selectAuthAccessToken);
  const ssoId = useSelector(selectProfileSSOid);
  const intl = useIntl();
  const { listing, isFetching, space, amenityOptions } = useListingDetailsData({
    listingId,
  });

  // requireAuth's redirect is a side effect, not a render gate
  // — bail out here so nothing real renders for a
  // logged-out request while that navigation is in flight.
  if (!accessToken) {
    return null;
  }

  const isLoading =
    isFetching || !listing || !space || !amenityOptions || !ssoId;

  // Ensure only listing owners can access this page
  if (!isLoading && listing.ownerId !== ssoId) {
    window.location.href = "/mylistings";
    return null;
  }

  return (
    <div
      className="ps-container-lg-margin ps-container-fixed"
      data-testid="listing-details-page"
    >
      <div className="ps-row">
        <div className="col-xs-12">
          {isLoading ? (
            <ListingEditorLayoutSkeleton />
          ) : (
            <ListingEditorLayout
              listing={listing}
              space={space}
              testIdPrefix="listing-details-page"
              pageHeader={intl.formatMessage({
                id: "hosting.listing_details.page_header",
                defaultMessage: "Listing details",
              })}
            >
              <ListingDetailsForm
                listing={listing}
                amenityOptions={amenityOptions}
              />
            </ListingEditorLayout>
          )}
        </div>
      </div>
    </div>
  );
};

export { ListingDetailsPage };
